import type { IAttachmentChange, IAttachmentState } from './attachmentChanges.types';
import { attachmentChangesDiff } from './attachmentChangesDiff';

export interface IAttachmentMonitorOptions {
    documentId: string;
    fetchState: (signal: AbortSignal) => Promise<IAttachmentState>;
    isOwn: (id?: string) => boolean;
    onState: (state: IAttachmentState, changes: IAttachmentChange[], reset: boolean) => void;
    onUnavailable: () => void;
    onHash: (hash: string) => void;
}

/** Single flight and a dirty bit close the push-during-fetch race without parallel snapshots. */
export class AttachmentChangesMonitor {
    private baseline?: IAttachmentState;
    private stopped = false;
    private running = false;
    private dirty = false;
    private timer?: ReturnType<typeof setTimeout>;
    private abort = new AbortController();
    private failures = 0;

    constructor(private readonly options: IAttachmentMonitorOptions) {}

    invalidate = () => {
        if (this.stopped) return;
        this.dirty = true;
        if (!this.running && !this.timer) this.timer = setTimeout(() => void this.read(), this.baseline ? 300 : 0);
    };

    stop() {
        this.stopped = true;
        clearTimeout(this.timer);
        this.abort.abort();
    }

    private async read() {
        this.timer = undefined;
        if (this.stopped || this.running) return;
        this.running = true;
        this.dirty = false;
        try {
            const next = await this.options.fetchState(this.abort.signal);
            if (this.stopped || next.documentId !== this.options.documentId) return;
            this.failures = 0;
            const previous = this.baseline;
            const reset = !previous || previous.accessScopeToken !== next.accessScopeToken;
            const changes = previous ? attachmentChangesDiff(previous, next, this.options.isOwn) : [];
            this.baseline = next;
            this.options.onHash(next.sourceDocumentHash);
            this.options.onState(next, changes, reset);
        } catch (error) {
            if (this.stopped) return;
            const status = (error as { response?: { status?: number } }).response?.status;
            if (status === 401 || status === 403 || status === 404) {
                this.stop();
                this.options.onUnavailable();
            } else if (++this.failures <= 3) {
                // Conflicts are retried in a fresh request/transaction. Other failures back off.
                this.timer = setTimeout(() => void this.read(), status === 409 ? 300 : this.failures * 2000);
            }
        } finally {
            this.running = false;
            if (this.dirty && !this.stopped && !this.timer) this.invalidate();
        }
    }
}
